// Search, meaning "take me there".
//
// It searches the two things this app can honestly search from a header: the
// screens, and the accounts connected to this workspace. It does NOT search
// posts, captions or media — those live behind their own queries on their own
// screens, and a box that appeared to search everything while quietly covering
// a nav list would be worse than no box at all. The placeholder says so.
//
// The list it searches is the favourites picker's list of candidates, which is
// exactly the same question asked for a different reason: everything this
// person could go to. Building it twice was how the two would have come to
// disagree about which screens a role can see.

import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AccountMark } from "../components/AccountMark";
import { SearchIcon } from "./icons";
import type { FavouriteItem } from "./state";

export function JumpSearch({ candidates }: { candidates: FavouriteItem[] }) {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [cursor, setCursor] = useState(0);
  const box = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  const hits = useMemo<FavouriteItem[]>(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    return candidates
      .filter((h) => h.label.toLowerCase().includes(q) || h.hint.toLowerCase().includes(q))
      .slice(0, 8);
  }, [query, candidates]);

  useEffect(() => { setCursor(0); }, [query]);

  // Clicking anywhere else puts the list away. Pointerdown rather than click,
  // so the list is gone before the thing underneath it reacts.
  useEffect(() => {
    if (!open) return;
    const away = (e: PointerEvent) => {
      if (!box.current?.contains(e.target as Node)) setOpen(false);
    };
    window.addEventListener("pointerdown", away);
    return () => window.removeEventListener("pointerdown", away);
  }, [open]);

  function go(hit: FavouriteItem | undefined) {
    if (!hit) return;
    setQuery("");
    setOpen(false);
    navigate(hit.route);
  }

  const showing = open && hits.length > 0;

  return (
    <div className="jump" ref={box}>
      <SearchIcon className="jump-icon" />
      <input
        type="search"
        className="jump-input"
        role="combobox"
        aria-expanded={showing}
        aria-controls="jump-results"
        aria-autocomplete="list"
        aria-activedescendant={showing ? `jump-hit-${cursor}` : undefined}
        placeholder="Search screens and accounts…"
        aria-label="Search screens and connected accounts"
        value={query}
        onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onKeyDown={(e) => {
          if (e.key === "Escape") { setOpen(false); return; }
          if (!showing) return;
          if (e.key === "ArrowDown") { e.preventDefault(); setCursor((c) => (c + 1) % hits.length); }
          if (e.key === "ArrowUp") { e.preventDefault(); setCursor((c) => (c - 1 + hits.length) % hits.length); }
          if (e.key === "Enter") { e.preventDefault(); go(hits[cursor]); }
        }}
      />

      {open && query.trim() !== "" && (
        <ul className="jump-results card" id="jump-results" role="listbox"
            aria-label="Search results">
          {hits.length === 0 ? (
            <li className="jump-empty muted" role="presentation">
              Nothing here by that name. Posts, captions and media are searched on their own
              screens.
            </li>
          ) : hits.map((hit, i) => {
            const Icon = hit.icon;
            return (
              <li key={hit.key} id={`jump-hit-${i}`} role="option" aria-selected={i === cursor}>
                <button type="button" className={i === cursor ? "jump-hit on" : "jump-hit"}
                        onMouseEnter={() => setCursor(i)}
                        onClick={() => go(hit)}>
                  {hit.connection
                    ? <AccountMark connection={hit.connection} />
                    : Icon ? <Icon className="nav-icon" /> : null}
                  <span className="jump-hit-text">
                    <strong>{hit.label}</strong>
                    <span className="muted">{hit.hint}</span>
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
